import { Box, Stack, Typography, Link } from "@mui/material"
import { NumericFormat } from "react-number-format"
import { useState, useContext } from "react"
import { useTheme } from "@mui/material/styles"
import { ChatIconCTA, FC, SpecialTag, Button, TripData, InquiryModal, TripModal } from ".."
import StickyTripBannerWrapper from "./StickyTripBannerWrapper"
import { SiteMetadataContext } from "../../local/wrappers/SiteMetadataProvider"
import TripCTA from "./TripCTA"

type StickyTripBannerProps = {
  duration: string
  price: number
  prefix: string
  savings: string
  onClick: () => void
  trip?: TripData | null
}

const StickyTripBannerMobile: FC<StickyTripBannerProps> = ({
  duration,
  price,
  prefix,
  savings,
  trip,
  onClick,
}) => {
  const isPriceAvailable = price && price > 200
  const siteMetadata = useContext(SiteMetadataContext)
  const theme = useTheme()
  const [openInquiryModal, setOpenInquiryModal] = useState(false)
  const [openTripModal, setOpenTripModal] = useState(false)

  return (
    <>
      <StickyTripBannerWrapper>
        <Stack direction="column" width="100%" spacing={4}>
          {/* Row 1: Duration, Price, Savings */}
          <Box display="flex" alignItems="center" gap={2} width="100%">
            {savings && (
              <SpecialTag
                sx={{
                  position: "relative",
                  top: "unset",
                  left: "unset",
                  width: "fit-content",
                  padding: "4px 6px",
                  flexShrink: 0,
                }}
              />
            )}
            <Stack minWidth={0} direction="row" alignItems="center" gap={2}>
              {duration && (
                <Typography
                  variant="body.md"
                  fontSize="12px"
                  fontWeight={600}
                  textTransform="uppercase"
                  sx={(theme) => ({ 
                    color: theme.palette["neutral.100"].main,
                    whiteSpace: "nowrap" 
                  })}
                >
                  {duration}
                </Typography>
              )}
              {isPriceAvailable ? (
                <Box
                  sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: theme.palette["neutral.100"].main,
                  })}
                >
                  <span
                    style={{
                      fontSize: "11px",
                      fontStyle: "normal",
                      fontWeight: 600,
                      lineHeight: "140%",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <NumericFormat
                      value={price}
                      displayType="text"
                      thousandSeparator
                      prefix={prefix}
                      decimalScale={0}
                    />
                  </span>
                </Box>
              ) : (
                <Typography
                  variant="body.md"
                  sx={(theme) => ({
                    fontSize: "11px!important",
                    fontStyle: "normal",
                    fontWeight: 600,
                    lineHeight: "140%",
                    color: theme.palette["neutral.100"].main,
                    whiteSpace: "nowrap",
                  })}
                >
                  Please Inquire
                </Typography>
              )}
            </Stack>
          </Box>
          {/* Row 2: Phone and Quote Button */}
          <Box display="flex" alignItems="center" gap={2} width="100%">
            <Box
              component="span"
              sx={(theme) => ({
                ...theme.typography["link.md"],
                backgroundColor: theme.palette["primary.800"].main,
                borderRadius: 1,
                lineHeight: 1,
                transition: theme.transitions.create(["background-color", "border-color", "color"]),
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: theme.palette["primary.900"].main,
                },
              })}
            >
              <Link
                href={siteMetadata.phoneUrl}
                variant="link.sm"
                display="flex"
                alignItems="center"
                minHeight="50px"
                gap="8px"
                color={(theme) => theme.palette["neutral.100"].main}
                underline="none"
                sx={{
                  textDecoration: "none",
                  padding: {
                    xs: theme.spacing(2, 3),
                    xl: theme.spacing(3, 5),
                  },
                  whiteSpace: "nowrap",
                }}
              >
                {siteMetadata.phone}
              </Link>
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              <TripCTA onClick={onClick} />
            </Box>
            {/* chat icon button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: {
                  xs: 2,
                  xl: 2.5,
                },
                color: theme.palette["neutral.100"].main,
                fontWeight: 600,
                lineHeight: "140%",

                "#chat-button": {
                  position: {
                    xs: "absolute",
                    md: "static",
                  },
                  bottom: {
                    xs: "50%",
                    md: "115px"
                  },
                  transform: {
                    xs: "translateY(-50%) !important",
                    md: "none"
                  },
                  width: 62,
                  height: 62,
                  boxShadow: "none",
                  display: "flex !important",
                  opacity: 1,
                  transition: "none !important",
                },

                "#chat-button > div": {
                  alignItems: "center",
                },

                "#chat-button svg": {
                  width: 22,
                  height: 22,
                },

                "#chat-button p": {
                  lineHeight: 1.5,
                },
              }}
            >
              <Typography variant="body.md" component="span" sx={{
                fontWeight: 600,
                display: {
                  xs: "none",
                  sm: "none",
                  md: "flex",
                },
                fontSize: {
                  md: "14px",
                  lg: "16px",
                  xl: "18px",
                },
              }} >
                Live Chat Now
              </Typography>
              <ChatIconCTA />
            </Box>
          </Box>
        </Stack>
      </StickyTripBannerWrapper>
      <InquiryModal open={openInquiryModal} onClose={() => setOpenInquiryModal(false)} />
      {trip && (
        <TripModal
          open={openTripModal}
          onClose={() => setOpenTripModal(false)}
          trip={trip as TripData}
        />
      )}
    </>
  )
}

export default StickyTripBannerMobile
